import React, { useEffect, useRef, useState } from "react";
import { Download, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const PAGE = "/crm/ad-materials";
const errMsg = (err) => err?.response?.data?.detail || err?.response?.data?.message || err?.message || "操作失败，请重试";
const attachmentKind = (name = "", contentType = "") => {
  const ext = String(name).split(".").pop().toLowerCase();
  if (ext === "pdf" || contentType === "application/pdf") return "pdf";
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return "photo";
  return "file";
};
const verifiedPdfBlob = async (data) => {
  if (!(data instanceof Blob)) throw new Error("附件内容无法预览，请下载原件核对。");
  const header = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("附件内容无法读取，请下载原件核对。"));
    reader.readAsText(data.slice(0, 5));
  });
  if (header !== "%PDF-") throw new Error("附件内容不是有效的 PDF，无法预览，请下载原件核对。");
  return new Blob([data], { type: "application/pdf" });
};
export default function AdMaterialAttachmentGallery({ fileIds, api, onDownload }) {
  const [files, setFiles] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [active, setActive] = useState(null);
  const [retry, setRetry] = useState(0);
  const activeUrl = useRef(null);
  const openAttempt = useRef(0);
  const previewTrigger = useRef(null);
  const idsKey = (fileIds || []).join("|");
  const closePreview = () => {
    openAttempt.current += 1;
    if (activeUrl.current) URL.revokeObjectURL(activeUrl.current);
    activeUrl.current = null;
    setActive(null);
  };

  useEffect(() => {
    if (!idsKey) { setFiles([]); setThumbnails({}); return; }
    const controller = new AbortController();
    let current = true;
    const urls = [];
    setFiles([]);
    setThumbnails({});
    Promise.all(idsKey.split("|").map(async (id) => {
      try {
        const { data } = await api.get(`${PAGE}/files/${id}/meta`, { signal: controller.signal });
        return { ...data, id, kind: attachmentKind(data.filename, data.contentType) };
      } catch (error) { return { id, error: errMsg(error) }; }
    })).then((rows) => {
      if (!current) return;
      setFiles(rows);
      rows.filter((file) => file.kind === "photo").forEach(async (file) => {
        try {
          const { data } = await api.get(`${PAGE}/files/${file.id}/thumb`, { responseType: "blob", signal: controller.signal });
          if (!current) return;
          const url = URL.createObjectURL(data);
          urls.push(url);
          setThumbnails((previous) => ({ ...previous, [file.id]: url }));
        } catch { /* The filename remains available when a thumbnail cannot load. */ }
      });
    });
    return () => {
      current = false;
      controller.abort();
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [api, idsKey, retry]);

  useEffect(() => () => {
    openAttempt.current += 1;
    if (activeUrl.current) URL.revokeObjectURL(activeUrl.current);
  }, []);

  const openPreview = async (file, trigger) => {
    if (!["photo", "pdf"].includes(file.kind)) return;
    previewTrigger.current = trigger;
    closePreview();
    const attempt = ++openAttempt.current;
    setActive({ id: file.id, loading: true });
    try {
      const { data } = await api.get(`${PAGE}/files/${file.id}`, { responseType: "blob" });
      if (attempt !== openAttempt.current) return;
      const safeData = file.kind === "pdf" ? await verifiedPdfBlob(data) : data;
      if (attempt !== openAttempt.current) return;
      const url = URL.createObjectURL(safeData);
      activeUrl.current = url;
      setActive({ id: file.id, url, name: file.filename, kind: file.kind });
    } catch (error) {
      if (attempt === openAttempt.current) setActive({ id: file.id, error: errMsg(error) });
    }
  };

  if (!fileIds?.length) return <p className="mt-1 text-sm text-slate-500">未上传附件</p>;
  return <>
    {!files.length ? <p role="status" className="mt-2 text-sm text-slate-500">正在读取附件…</p> : <div className="mt-2 grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="ad-material-attachment-gallery">
      {files.map((file, index) => <div key={file.id} className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50/70">
        {file.error ? <div className="flex aspect-[4/3] items-center justify-center px-2 text-center text-xs text-rose-700">附件 {index + 1} 读取失败</div> : <button type="button" onClick={(event) => openPreview(file, event.currentTarget)} disabled={!["photo", "pdf"].includes(file.kind)} aria-label={["photo", "pdf"].includes(file.kind) ? `放大查看 ${file.filename}` : file.filename} data-testid={`ad-material-attachment-${file.id}`} className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-white text-slate-500 transition hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400 disabled:cursor-default disabled:hover:bg-white">
          {file.kind === "photo" && thumbnails[file.id] ? <img src={thumbnails[file.id]} alt="" className="h-full w-full object-contain" /> : <FileText aria-hidden="true" className="h-7 w-7" />}
        </button>}
        <div className="flex min-w-0 items-center gap-1 px-2 py-1.5"><span className="min-w-0 flex-1 truncate text-xs text-slate-700" title={file.filename || file.error}>{file.filename || `附件 ${index + 1}`}</span>{!file.error && !["photo", "pdf"].includes(file.kind) && <button type="button" className="shrink-0 rounded p-1 text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" aria-label={`下载 ${file.filename}`} onClick={() => onDownload(file.id)}><Download aria-hidden="true" className="h-4 w-4" /></button>}</div>
      </div>)}
    </div>}
    {files.some((file) => file.error) && <button type="button" className="mt-2 text-xs text-brand-700 underline focus-visible:ring-2 focus-visible:ring-brand-400" onClick={() => setRetry((value) => value + 1)}>重试读取附件</button>}
    {active?.loading && <p role="status" className="mt-2 text-xs text-slate-500">正在打开附件…</p>}
    {active?.error && <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-xs text-rose-700"><span>附件预览失败：{active.error}</span><button type="button" className="font-medium underline focus-visible:ring-2 focus-visible:ring-brand-400" onClick={() => onDownload(active.id)}>下载原件</button></div>}
    <Dialog open={!!active?.url} onOpenChange={(open) => { if (!open) closePreview(); }}>
      <DialogContent className="flex max-h-[94dvh] w-[calc(100%-1rem)] max-w-6xl flex-col gap-0 overflow-hidden p-0" data-testid="ad-material-attachment-preview" onCloseAutoFocus={(event) => { event.preventDefault(); previewTrigger.current?.focus(); }}>
        <DialogHeader className="border-b border-slate-100 px-4 py-3"><DialogTitle className="truncate pr-10 text-base" title={active?.name}>{active?.name || "附件预览"}</DialogTitle><DialogDescription>预览附件，关闭后返回采购申请。</DialogDescription></DialogHeader>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-slate-950 p-2 sm:p-4">
          {active?.kind === "photo" && <img src={active.url} alt={active.name} className="max-h-[78dvh] max-w-full object-contain" data-testid="ad-material-attachment-preview-image" onError={() => { if (activeUrl.current === active.url) { URL.revokeObjectURL(active.url); activeUrl.current = null; setActive({ id: active.id, error: "图像内容无法显示，请下载原件核对。" }); } }} />}
          {active?.kind === "pdf" && <iframe src={active.url} title={active.name} sandbox="" referrerPolicy="no-referrer" className="h-[78dvh] w-full rounded bg-white" data-testid="ad-material-attachment-preview-pdf" />}
        </div>
        <div className="flex justify-end border-t border-slate-100 px-4 py-2"><Button type="button" variant="outline" className="min-h-10" onClick={() => onDownload(active.id)}><Download aria-hidden="true" className="mr-1.5 h-4 w-4" />下载原件</Button></div>
      </DialogContent>
    </Dialog>
  </>;
}
